import classNames from 'classnames'
import { JSX, useEffect, useRef, useState } from 'react'

import { t } from '../../../common/i18n.js'
import {
    ApplicationId,
    ApplicationRole,
    applicationRoleRank,
} from '../../../common/types/common.js'
import type { Language } from '../../../common/types/i18n.js'
import { Link } from '../link/link.js'
import { ThemeSwitch, ThemeSwitchProps } from '../theme-switch/theme-switch.js'
import './top-navigation.scss'

export interface TopNavItem {
    text: string
    url?: string
    href?: string // external link
    isActive?: boolean
    isButton?: boolean // renders as accent button (for Q12/campaign)
    onClick?: () => void
}

export interface TopNavigationProps {
    items: TopNavItem[]
    logo?: { src: string; alt: string }
    languageButtons?: { text: string; isActive?: boolean; onClick?: () => void }[]
    user?: {
        name: string
        role: string
        applicationRoles: Record<ApplicationId, ApplicationRole>
        isManager?: boolean
        onLogout: () => void
    }
    /** Teemavalik kasutajamenüüs; puudumisel rida ei näidata. */
    theme?: Pick<ThemeSwitchProps, 'value' | 'onChange' | 'labels'>
    lang: Language
}

export const TopNavigation = (props: TopNavigationProps): JSX.Element => {
    const [menuOpen, setMenuOpen] = useState(false)
    const [langOpen, setLangOpen] = useState(false)
    const [userOpen, setUserOpen] = useState(false)

    const langRef = useRef<HTMLDivElement>(null)
    const userRef = useRef<HTMLDivElement>(null)

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (langRef.current && !langRef.current.contains(e.target as Node)) {
                setLangOpen(false)
            }
            if (userRef.current && !userRef.current.contains(e.target as Node)) {
                setUserOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // Lock body scroll when mobile menu is open
    useEffect(() => {
        if (menuOpen) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = ''
        }
        return () => {
            document.body.style.overflow = ''
        }
    }, [menuOpen])

    const activeLang = props.languageButtons?.find((b) => b.isActive)
    const canAccessAdmin =
        props.user &&
        Object.entries(props.user.applicationRoles).some(
            ([applicationId, role]) =>
                applicationRoleRank[role] >= applicationRoleRank.admin ||
                (applicationId === 'q12' &&
                    applicationRoleRank[role] >= applicationRoleRank.manager),
        )

    return (
        <header className="top-navigation">
            <div className="top-navigation__inner h-container">
                {props.logo && (
                    <Link href="/" className="top-navigation__logo">
                        <img src={props.logo.src} alt={props.logo.alt} />
                    </Link>
                )}
                <nav
                    className={classNames('top-navigation__nav', {
                        'top-navigation__nav--open': menuOpen,
                    })}
                >
                    <ul className="top-navigation__list">
                        {props.items.map((item, i) => (
                            <li
                                key={i}
                                className={classNames('top-navigation__item', {
                                    'is-active': item.isActive,
                                    'top-navigation__item--button': item.isButton,
                                })}
                            >
                                {item.href ? (
                                    <Link
                                        href={item.href}
                                        external
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="top-navigation__link"
                                    >
                                        {item.text}
                                    </Link>
                                ) : item.url ? (
                                    <Link
                                        href={item.url}
                                        className="top-navigation__link"
                                        onClick={(e) => {
                                            // An item with its own onClick handles
                                            // navigation itself — suppress Link's.
                                            if (item.onClick) {
                                                e.preventDefault()
                                                item.onClick()
                                            }
                                            setMenuOpen(false)
                                        }}
                                    >
                                        {item.text}
                                    </Link>
                                ) : (
                                    <button
                                        className="top-navigation__link"
                                        onClick={() => {
                                            item.onClick?.()
                                            setMenuOpen(false)
                                        }}
                                    >
                                        {item.text}
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className="top-navigation__right">
                    {/* Language dropdown */}
                    {props.languageButtons && props.languageButtons.length > 0 && (
                        <div className="top-navigation__dropdown" ref={langRef}>
                            <button
                                className="top-navigation__dropdown-trigger top-navigation__lang-trigger"
                                onClick={() => {
                                    setLangOpen(!langOpen)
                                    setUserOpen(false)
                                }}
                            >
                                {activeLang?.text ?? props.languageButtons[0].text}
                                <span className="top-navigation__dropdown-arrow">▾</span>
                            </button>
                            {langOpen && (
                                <div className="top-navigation__dropdown-menu">
                                    {props.languageButtons.map((btn, i) => (
                                        <button
                                            key={i}
                                            className={classNames('top-navigation__dropdown-item', {
                                                'is-active': btn.isActive,
                                            })}
                                            onClick={() => {
                                                btn.onClick?.()
                                                setLangOpen(false)
                                            }}
                                        >
                                            {btn.text}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* User dropdown */}
                    {props.user && (
                        <div className="top-navigation__dropdown" ref={userRef}>
                            <button
                                className="top-navigation__dropdown-trigger top-navigation__user-trigger"
                                onClick={() => {
                                    setUserOpen(!userOpen)
                                    setLangOpen(false)
                                }}
                            >
                                {props.user.name}
                                <span className="top-navigation__dropdown-arrow">▾</span>
                            </button>
                            {userOpen && (
                                <div className="top-navigation__dropdown-menu">
                                    {canAccessAdmin && (
                                        <Link
                                            href="/admin"
                                            external
                                            className="top-navigation__dropdown-item"
                                            onClick={() => setUserOpen(false)}
                                        >
                                            {t.common.admin(props.lang)}
                                        </Link>
                                    )}
                                    {props.theme && (
                                        <div className="top-navigation__dropdown-theme">
                                            <ThemeSwitch
                                                {...props.theme}
                                                size="compact"
                                                tone="chrome"
                                            />
                                        </div>
                                    )}
                                    <button
                                        className="top-navigation__dropdown-item"
                                        onClick={() => {
                                            props.user!.onLogout()
                                            setUserOpen(false)
                                        }}
                                    >
                                        {t.common.logOut(props.lang)}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Burger (mobile — right side) */}
                    <button
                        className="top-navigation__burger"
                        onClick={() => setMenuOpen(!menuOpen)}
                        aria-label={t.common.toggleMenu(props.lang)}
                    >
                        <span className="top-navigation__burger-line" />
                        <span className="top-navigation__burger-line" />
                        <span className="top-navigation__burger-line" />
                    </button>
                </div>
            </div>
        </header>
    )
}
